import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import { useNavigate } from 'react-router-dom';
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react';
import { useGetKidOverviewHistoricalData } from '../../../../../api/kids/hooks/queries';
import { cn } from '../../../../../utilities/cn';
import { FaChevronDown } from 'react-icons/fa';
import { KidDocumentStatus, KidShareClass } from '../types/KidEditorV2.types';
import { zIndexes } from '../../../../../styling/zIndexes';

interface VersionSelectorProps extends React.HTMLAttributes<HTMLDivElement> {
  shareClass: KidShareClass;
  fundId: string;
  clientId: string;
}

// --------------------------------------------------------------------------
// Inner panel — selection state resets on close/unmount automatically
// --------------------------------------------------------------------------

interface VersionSelectorContentProps {
  groupedVersions: Record<string, string[]>;
  currentVersion: string;
  isLoading: boolean;
  shareClass: KidShareClass;
  fundId: string;
  close: () => void;
}

const VersionSelectorContent: FunctionComponent<VersionSelectorContentProps> = ({
  groupedVersions,
  currentVersion,
  isLoading,
  shareClass,
  fundId,
  close,
}) => {
  const navigate = useNavigate();
  const [selectedVersion, setSelectedVersion] = useState<string>(currentVersion);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupsInitialized, setGroupsInitialized] = useState(false);

  useEffect(() => {
    if (groupsInitialized) return;
    const majorVersions = Object.keys(groupedVersions).sort((a, b) => Number(b) - Number(a));
    if (majorVersions.length === 0) return;
    setExpandedGroups(new Set([majorVersions[0]]));
    setGroupsInitialized(true);
  }, [groupedVersions, groupsInitialized]);

  const toggleGroup = (majorVersion: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(majorVersion) ? next.delete(majorVersion) : next.add(majorVersion);
      return next;
    });
  };

  const handleView = () => {
    if (!selectedVersion) return;
    const params = new URLSearchParams({
      fundId,
      shareClassId: shareClass.share_class_id,
      documentLanguage: shareClass.document_language || 'English',
      version: selectedVersion,
    });
    close();
    navigate(`${RaidrV2RoutePaths.KID_VIEW_V2}?${params.toString()}`);
  };

  if (isLoading) {
    return (
      <div className="p-3">
        <div className="mb-3 border-b border-gray-200 pb-2 text-sm font-semibold text-mainblue">
          Select Version
        </div>
        <div className="space-y-3" role="status" aria-label="Loading versions">
          {[1, 2, 3].map((groupIndex) => (
            <div key={groupIndex} className="space-y-1">
              <div className="px-2">
                <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
              </div>
              {[1, 2].map((itemIndex) => (
                <div key={itemIndex} className="flex items-center gap-2 rounded px-2 py-1.5">
                  <div className="h-3 w-16 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </div>
          ))}
          <span className="sr-only">Loading versions...</span>
        </div>
      </div>
    );
  }

  const majorVersions = Object.keys(groupedVersions).sort((a, b) => Number(b) - Number(a));

  return (
    <div className="p-3">
      <div className="mb-3 border-b border-gray-200 pb-2 text-sm font-semibold text-mainblue">
        Select Version
      </div>

      {majorVersions.length === 0 ? (
        <div className="py-4 text-center text-xs text-gray-500">No versions available</div>
      ) : (
        <div className="scrollbar-custom h-40 overflow-y-auto">
          <div className="space-y-3">
            {majorVersions.map((majorVersion) => {
              const versionList = groupedVersions[majorVersion];
              const isExpanded = expandedGroups.has(majorVersion);

              return (
                <div key={majorVersion} className="space-y-1">
                  <button
                    onClick={() => toggleGroup(majorVersion)}
                    className="flex w-full items-center justify-between border-b border-gray-200 px-2 pb-2 text-xs font-semibold text-gray-500 hover:text-gray-700"
                  >
                    <span>
                      v{majorVersion}
                      <span className="ml-1.5 font-normal text-gray-400">
                        ({versionList.length} version{versionList.length === 1 ? '' : 's'})
                      </span>
                    </span>
                    <FaChevronDown
                      className={`h-3 w-3 text-blue-600 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    />
                  </button>

                  {isExpanded && versionList.map((version) => {
                    const isSelected = version === selectedVersion;
                    return (
                      <button
                        key={version}
                        onClick={() => setSelectedVersion(version)}
                        className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs transition-colors ${isSelected
                          ? 'bg-blue-100 font-medium text-gray-700'
                          : 'text-gray-700 hover:bg-gray-50'
                          }`}
                      >
                        <div
                          className={`flex h-4 w-4 flex-shrink-0 items-center justify-center rounded border ${isSelected ? 'border-blue-600 bg-blue-600' : 'border-gray-300'
                            }`}
                        >
                          {isSelected && (
                            <svg
                              className="h-3 w-3 text-white"
                              fill="none"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                        </div>
                        <span>v{version}</span>
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-3 border-t border-gray-200 pt-3">
        <div className="mb-2 text-xs font-semibold text-mainblue">
          Selected: {selectedVersion ? `v${selectedVersion}` : '—'}
        </div>
        <button
          onClick={handleView}
          disabled={!selectedVersion}
          className="flex w-full items-center justify-center rounded-md bg-mainblue px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-mainblue/90 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:opacity-50"
        >
          View
        </button>
      </div>
    </div>
  );
};

// --------------------------------------------------------------------------
// Outer component — data fetching and popover shell only
// --------------------------------------------------------------------------

const VersionSelector: FunctionComponent<VersionSelectorProps> = ({
  shareClass,
  fundId,
  clientId,
  ...props
}) => {
  const currentVersion =
    shareClass.version_number && shareClass.version_number !== '-'
      ? shareClass.version_number
      : '';

  const hasDocument = shareClass.status !== KidDocumentStatus.NoDocument;

  const { data: historicalData, isLoading } = useGetKidOverviewHistoricalData(
    clientId,
    fundId,
    shareClass.share_class_id,
    shareClass.document_language,
    {
      enabled:
        hasDocument &&
        !!clientId &&
        !!fundId &&
        !!shareClass.share_class_id,
    },
  );

  const groupedVersions = useMemo(() => {
    const versionSet = new Set<string>();

    if (currentVersion) versionSet.add(currentVersion);

    if (historicalData) {
      Object.values(historicalData).forEach((items) => {
        items.forEach((item) => {
          if (item.version_number) versionSet.add(item.version_number);
        });
      });
    }

    const sortedVersions = Array.from(versionSet).sort((a, b) => {
      const aParts = a.split('.').map(Number);
      const bParts = b.split('.').map(Number);
      for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
        const diff = (bParts[i] || 0) - (aParts[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });

    const grouped: Record<string, string[]> = {};
    sortedVersions.forEach((version) => {
      const major = version.split('.')[0];
      if (!grouped[major]) grouped[major] = [];
      grouped[major].push(version);
    });

    return grouped;
  }, [historicalData, currentVersion]);

  if (shareClass.version_number === '-') {
    return <span className="text-xs text-gray-500">-</span>;
  }

  return (
    <Popover className="relative">
      {({ close }) => (
        <>
          <PopoverButton
            disabled={!currentVersion}
            className={cn(
              'w-24 rounded-md border border-gray-300 bg-white px-2 py-1 text-left text-xs font-medium transition-colors hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-500',
              !currentVersion && 'cursor-not-allowed opacity-50',
              props.className,
            )}
          >
            <span className="block truncate">
              {isLoading && Object.keys(groupedVersions).length === 0
                ? 'Loading...'
                : currentVersion
                  ? `v${currentVersion}`
                  : 'Select version'}
            </span>
          </PopoverButton>

          <PopoverPanel
            anchor={{ to: 'bottom', gap: 4, padding: 16 }}
            style={{ zIndex: zIndexes.floatingPanel }}
            className="w-48 origin-top overflow-visible rounded-lg border border-gray-200 bg-white shadow-lg focus:outline-none [--anchor-max-height:none]"
          >
            <VersionSelectorContent
              groupedVersions={groupedVersions}
              currentVersion={currentVersion}
              isLoading={isLoading}
              shareClass={shareClass}
              fundId={fundId}
              close={close}
            />
          </PopoverPanel>
        </>
      )}
    </Popover>
  );
};

export default VersionSelector;
